import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { 
  EyeIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BanknotesIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { policyAPI, requestAPI, offerAPI, mutualisteAPI, enterpriseAPI, formatCurrency, getOfferTypeLabel, healthReimbursementAPI, healthServiceAPI, uploadAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SavingsDetailsModal from '../components/SavingsDetailsModal';
import HealthPolicyDetailsModal from '../components/HealthPolicyDetailsModal';
import toast from 'react-hot-toast';

const AdminPolicies = () => {
  const queryClient = useQueryClient();
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSavingsModal, setShowSavingsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // Modal de résiliation non utilisé actuellement (conservé pour usage futur éventuel)
  // const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [showHealthReimbursementModal, setShowHealthReimbursementModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationNotes, setTerminationNotes] = useState('');
  const [filters, setFilters] = useState({
    product: '',
    status: '',
    mutualiste: '',
    enterprise: '',
    startDate: '',
    endDate: ''
  });
  const [mutualisteSearch, setMutualisteSearch] = useState('');
  const [mutualistes, setMutualistes] = useState([]);
  const [showMutualisteDropdown, setShowMutualisteDropdown] = useState(false);
  
  const [enterpriseSearch, setEnterpriseSearch] = useState('');
  const [enterprises, setEnterprises] = useState([]);
  const [showEnterpriseDropdown, setShowEnterpriseDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  const [expandedContributionYears, setExpandedContributionYears] = useState({});
  const [reimbursementForm, setReimbursementForm] = useState({
    reimbursementType: '',
    typeId: '',
    amountSurcout: '',
    amountTicketModerateur: '',
    amountTotalRequested: '',
    reasonText: '',
    beneficiaryId: '',
    serviceTypeId: '',
    executedAt: '',
    etablissement: '',
    praticien: '',
    costTotal: '',
    costConventionne: '',
    assurancePrincipalePart: '',
    ticketModerateur: '',
    surcout: ''
  });

  // Récupérer les polices
  const { data: policiesData, isLoading } = useQuery(
    ['admin-policies', filters, currentPage],
    () => policyAPI.getPolicies({ 
      status: filters.status || undefined,
      offerId: filters.product || undefined,
      mutualisteId: filters.mutualiste || undefined,
      enterpriseId: filters.enterprise || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      page: currentPage,
      limit: 10 
    }),
    {
      refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
    }
  );

  // Mutation pour créer une demande de remboursement santé
  const createHealthReimbursementMutation = useMutation(
    (data) => healthReimbursementAPI.createHealthReimbursement(data),
    {
      onSuccess: () => {
        toast.success('Demande de remboursement santé créée avec succès');
        queryClient.invalidateQueries('admin-requests');
        setShowHealthReimbursementModal(false);
        setReimbursementForm({
          reimbursementType: '',
          typeId: '',
          amountSurcout: '',
          amountTicketModerateur: '',
          amountTotalRequested: '',
          reasonText: '',
          beneficiaryId: '',
          serviceTypeId: '',
          executedAt: '',
          etablissement: '',
          praticien: '',
          costTotal: '',
          costConventionne: '',
          assurancePrincipalePart: '',
          ticketModerateur: '',
          surcout: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la création de la demande de remboursement santé');
      }
    }
  );

  const handleCreateHealthReimbursement = async () => {
    if (!selectedPolicy) {
      toast.error('Aucune police sélectionnée');
      return;
    }

    const {
      beneficiaryId,
      serviceTypeId,
      executedAt,
      etablissement,
      praticien,
      costTotal,
      costConventionne,
      assurancePrincipalePart,
      ticketModerateur,
      surcout,
      typeId,
      amountTicketModerateur,
      amountSurcout,
      attachmentFile,
    } = reimbursementForm;

    if (
      !beneficiaryId ||
      !serviceTypeId ||
      !executedAt ||
      !etablissement ||
      !praticien ||
      !costTotal ||
      !costConventionne ||
      !assurancePrincipalePart ||
      !ticketModerateur ||
      !typeId ||
      !attachmentFile ||
      attachmentFile.length === 0
    ) {
      toast.error('Veuillez renseigner tous les champs obligatoires');
      return;
    }

    try {
      // Créer d'abord la prestation de santé
      const healthServiceResponse = await healthServiceAPI.createHealthService({
        policyId: selectedPolicy.id,
        mutualisteId: beneficiaryId,
        typeId: serviceTypeId,
        executedAt,
        etablissement,
        praticien,
        costTotal: parseFloat(costTotal) || 0,
        costConventionne: parseFloat(costConventionne) || 0,
        assurancePrincipalePart: parseFloat(assurancePrincipalePart) || 0,
        ticketModerateur: parseFloat(ticketModerateur) || 0,
        surcout: parseFloat(surcout) || 0
      });

      const healthService = healthServiceResponse.data?.data || healthServiceResponse.data;

      const selectedRequestType = requestTypes.find((t) => t.id === typeId);
      const reimbursementType = selectedRequestType?.libelle || 'Remboursement';

      // Normaliser les montants saisis (soit via les champs spécifiques amount*, soit via les montants de la prestation)
      const ticketValue = parseFloat(amountTicketModerateur || ticketModerateur) || 0;
      const surcoutValue = parseFloat(amountSurcout || surcout) || 0;

      // Calcul du total demandé en fonction du type de demande
      let amountTotalRequested = 0;
      if (reimbursementType === 'Remboursement de surcoût') {
        // 75% du surcoût
        amountTotalRequested = surcoutValue * 0.75;
      } else if (reimbursementType === 'Remboursement de ticket modérateur') {
        // 100% du ticket modérateur
        amountTotalRequested = ticketValue;
      } else if (reimbursementType === 'Remboursement de ticket modérateur et de surcoût') {
        // 75% du surcoût + 100% du ticket modérateur
        amountTotalRequested = (surcoutValue * 0.75) + ticketValue;
      } else {
        // Sécurité : par défaut, garder l'ancien comportement (surcoût + ticket)
        amountTotalRequested = surcoutValue + ticketValue;
      }

      // Uploader les pièces jointes et construire le tableau attachments
      let attachments = [];
      if (Array.isArray(attachmentFile) && attachmentFile.length > 0) {
        const formData = new FormData();
        attachmentFile.forEach((file) => {
          formData.append('files', file);
        });

        const uploadResponse = await uploadAPI.uploadHealthReimbursementFiles(formData);
        attachments = uploadResponse.data?.data || uploadResponse.data || [];
      }

      createHealthReimbursementMutation.mutate({
        policyId: selectedPolicy.id,
        typeId,
        healthServiceId: healthService.id,
        amountTicketModerateur: ticketValue,
        amountSurcout: surcoutValue,
        amountTotalRequested,
        reasonText: terminationNotes.trim() || 'Prestations FPM',
        reimbursementType,
        attachments,
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création de la prestation de santé');
    }
  };

  const openHealthReimbursementModal = () => {
    const adherent = selectedPolicy?.subscription?.mutualiste;
    setReimbursementForm({
      reimbursementType: '',
      typeId: '',
      amountSurcout: '',
      amountTicketModerateur: '',
      amountTotalRequested: '',
      reasonText: '',
      beneficiaryId: adherent?.id || '',
      serviceTypeId: '',
      executedAt: '',
      etablissement: '',
      praticien: '',
      costTotal: '',
      costConventionne: '',
      assurancePrincipalePart: '',
      ticketModerateur: '',
      surcout: ''
    });
    setShowHealthReimbursementModal(true);
  };

  const policies = policiesData?.data?.policies || [];
  const pagination = policiesData?.data?.pagination || {};

  // Récupérer les types de demande
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const { data: requestTypesRaw } = useQuery(
    'request-types',
    async () => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/request-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Le backend renvoie { success: true, data: [...] }
      return response.data;
    }
  );

  const requestTypes = Array.isArray(requestTypesRaw?.data)
    ? requestTypesRaw.data
    : Array.isArray(requestTypesRaw)
      ? requestTypesRaw
      : [];
  const terminationType = requestTypes.find(t => t.libelle.toLowerCase() === 'résiliation');
  // const healthReimbursementTypeTicket = requestTypes.find(t => t.libelle === 'Remboursement de ticket modérateur');
  // const healthReimbursementTypeSurcout = requestTypes.find(t => t.libelle === 'Remboursement de surcoût');
  // const healthReimbursementTypeBoth = requestTypes.find(t => t.libelle === 'Remboursement de ticket modérateur et de surcoût');

  const healthRequestTypes = requestTypes.filter(t =>
    typeof t.libelle === 'string' && t.libelle.startsWith('Remboursement')
  );

  // Récupérer les types de prestations de santé
  const { data: healthServiceTypesRaw } = useQuery(
    ['health-service-types'],
    () => healthServiceAPI.getHealthServiceTypes().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const healthServiceTypes = Array.isArray(healthServiceTypesRaw?.data)
    ? healthServiceTypesRaw.data
    : Array.isArray(healthServiceTypesRaw)
      ? healthServiceTypesRaw
      : [];

  // Récupérer les statistiques (filtrées)
  const { data: statsData } = useQuery(
    ['policy-stats', filters],
    () => policyAPI.getStats({
      status: filters.status || undefined,
      offerId: filters.product || undefined,
      mutualisteId: filters.mutualiste || undefined,
      enterpriseId: filters.enterprise || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined
    }),
    {
      refetchInterval: 60000, // Rafraîchir toutes les minutes
    }
  );

  const stats = statsData?.data || {};

  // Récupérer la liste des produits
  const { data: offersData } = useQuery(
    'offers-list',
    () => offerAPI.getOffers()
  );

  const offers = offersData?.data?.offers || offersData?.data || [];

  // Refs pour détecter les clics en dehors
  const mutualisteRef = useRef(null);
  const enterpriseRef = useRef(null);

  // Fermer les dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mutualisteRef.current && !mutualisteRef.current.contains(event.target)) {
        setShowMutualisteDropdown(false);
      }
      if (enterpriseRef.current && !enterpriseRef.current.contains(event.target)) {
        setShowEnterpriseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getPaginationItems = (current, total, maxVisible = 9) => {
    const items = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) items.push(i);
      return items;
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - half);
    let end = start + maxVisible - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) items.push(i);

    return items;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Mutation pour changer le statut d'une police
  const updateStatusMutation = useMutation(
    ({ id, status }) => policyAPI.updatePolicyStatus(id, { status }),
    {
      onSuccess: () => {
        toast.success('Statut de la police mis à jour avec succès');
        queryClient.invalidateQueries('admin-policies');
        queryClient.invalidateQueries('policy-stats');
        setShowStatusModal(false);
        setSelectedPolicy(null);
        setNewStatus('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
      }
    }
  );

  // Mutation pour créer une demande de résiliation
  const createRequestMutation = useMutation(
    (data) => requestAPI.createRequest(data),
    {
      onSuccess: () => {
        toast.success('Demande de résiliation créée avec succès');
        queryClient.invalidateQueries('admin-policies');
        // setShowTerminationModal(false);
        setSelectedPolicy(null);
        setTerminationReason('');
        setTerminationNotes('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la création de la demande');
      }
    }
  );

  const handleStatusChange = (policy) => {
    setSelectedPolicy(policy);
    setNewStatus(policy.status);
    setShowStatusModal(true);
  };

  const handleRequestTermination = (policy) => {
    setSelectedPolicy(policy);
    setSelectedTypeId(terminationType?.id || '');
    setTerminationReason('');
    setTerminationNotes('');
    // Ici on crée directement la demande sans modal intermédiaire, en réutilisant la logique existante
    if (!terminationType) {
      toast.error('Type de demande de résiliation introuvable');
      return;
    }
    createRequestMutation.mutate({
      policyId: policy.id,
      typeId: terminationType.id,
      reasonText: 'Demande de résiliation (admin)',
      notes: ''
    });
  };

  // const confirmTermination = () => {
  //   if (!selectedTypeId) {
  //     toast.error('Veuillez sélectionner un type de demande');
  //     return;
  //   }

  //   if (!terminationReason.trim()) {
  //     toast.error('Veuillez indiquer la raison de la demande');
  //     return;
  //   }

  //   createRequestMutation.mutate({
  //     policyId: selectedPolicy.id,
  //     typeId: selectedTypeId,
  //     reasonText: terminationReason,
  //     notes: terminationNotes
  //   });
  // };

  const handleViewDetails = async (policy) => {
    try {
      const response = await policyAPI.getPolicyDetails(policy.id);
      setSelectedPolicy(response.data);
      
      // Si c'est une police d'épargne, afficher le modal spécifique
      if (response.data.subscription?.offer?.type === 'SAVINGS') {
        setShowSavingsModal(true);
      } else {
        setShowDetailsModal(true);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Recherche de mutualistes avec debounce
  const searchMutualistes = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setMutualistes([]);
      setShowMutualisteDropdown(false);
      return;
    }

    try {
      console.log('🔍 Recherche mutualistes:', searchTerm);
      const response = await mutualisteAPI.searchMutualistes({ search: searchTerm });
      console.log('✅ Réponse mutualistes:', response.data);
      const mutualistesData = response.data?.data?.mutualistes || response.data?.mutualistes || [];
      console.log('📋 Mutualistes extraits:', mutualistesData);
      setMutualistes(mutualistesData);
      setShowMutualisteDropdown(true);
    } catch (error) {
      console.error('❌ Erreur recherche mutualistes:', error);
      console.error('❌ Response:', error.response?.data);
      setMutualistes([]);
    }
  };

  // Recherche d'entreprises avec debounce
  const searchEnterprises = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setEnterprises([]);
      setShowEnterpriseDropdown(false);
      return;
    }

    try {
      const response = await enterpriseAPI.search({ q: searchTerm });
      const enterprisesData = response.data?.data || [];
      setEnterprises(enterprisesData);
      setShowEnterpriseDropdown(true);
    } catch (error) {
      console.error('Erreur recherche entreprises:', error);
      setEnterprises([]);
    }
  };

  // Handlers pour la sélection
  const handleSelectMutualiste = (mutualiste) => {
    setMutualisteSearch(`${mutualiste.matricule} - ${mutualiste.nom} ${mutualiste.prenoms}`);
    setFilters(prev => ({ ...prev, mutualiste: mutualiste.id }));
    setShowMutualisteDropdown(false);
    setCurrentPage(1);
  };

  const handleSelectEnterprise = (enterprise) => {
    setEnterpriseSearch(enterprise.nom);
    setFilters(prev => ({ ...prev, enterprise: enterprise.id }));
    setShowEnterpriseDropdown(false);
    setCurrentPage(1);
  };

  const confirmStatusChange = () => {
    if (selectedPolicy && newStatus && newStatus !== selectedPolicy.status) {
      updateStatusMutation.mutate({
        id: selectedPolicy.id,
        status: newStatus
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'PENDING':
        return <PauseCircleIcon className="h-5 w-5 text-yellow-500" />;
      case 'SUSPENDED':
        return <XCircleIcon className="h-5 w-5 text-orange-500" />;
      case 'TERMINATED':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'SUSPENDED': return 'orange';
      case 'TERMINATED': return 'danger';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    // Si on reçoit par erreur un objet Status complet, en extraire le libellé
    if (status && typeof status === 'object') {
      return status.libelle || '';
    }

    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'PENDING':
        return 'En attente';
      case 'SUSPENDED':
        return 'Suspendue';
      case 'TERMINATED':
        return 'Résiliée';
      default:
        return status || '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Gestion des polices
        </h1>
        <p className="text-gray-600">
          Gérez les polices d'assurance générées après validation des souscriptions
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <DocumentTextIcon className="h-6 w-6 text-blue-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">Total</p>
            <p className="text-xl font-bold text-gray-900">{stats.totalPolicies || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">Actives</p>
            <p className="text-xl font-bold text-gray-900">{stats.activePolicies || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <PauseCircleIcon className="h-6 w-6 text-yellow-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">En attente</p>
            <p className="text-xl font-bold text-gray-900">{stats.pendingPolicies || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <XCircleIcon className="h-6 w-6 text-orange-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">Suspendues</p>
            <p className="text-xl font-bold text-gray-900">{stats.suspendedPolicies || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <svg className="h-6 w-6 text-red-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <p className="text-xs font-medium text-gray-500 mb-1">Résiliées</p>
            <p className="text-xl font-bold text-gray-900">{stats.terminatedPolicies || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center mb-2">
              <span className="text-green-600 font-bold text-sm">%</span>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">Taux paiement</p>
            <p className="text-xl font-bold text-gray-900">{stats.paymentRate || 0}%</p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Filtre Date début */}
          <div>
            <label className="form-label">Date début</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, startDate: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            />
          </div>

          {/* Filtre Date fin */}
          <div>
            <label className="form-label">Date fin</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, endDate: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            />
          </div>

          {/* Filtre Produit */}
          <div>
            <label className="form-label">Produit</label>
            <select
              value={filters.product}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, product: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            >
              <option value="">{String('Tous les produits')}</option>
              {offers.map(offer => (
                <option key={offer.id} value={offer.id}>
                  {String(offer.name)} - {String(getOfferTypeLabel(offer.type))}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre Statut */}
          <div>
            <label className="form-label">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            >
              <option value="">{String('Tous les statuts')}</option>
              <option value="PENDING">{String('En attente')}</option>
              <option value="ACTIVE">{String('Actives')}</option>
              <option value="SUSPENDED">{String('Suspendues')}</option>
              <option value="TERMINATED">{String('Résiliées')}</option>
            </select>
          </div>

          {/* Filtre Entreprise */}
          <div className="relative" ref={enterpriseRef}>
            <label className="form-label">Entreprise</label>
            <input
              type="text"
              value={enterpriseSearch}
              onChange={(e) => {
                const value = e.target.value;
                setEnterpriseSearch(value);
                if (!value) {
                  setFilters(prev => ({ ...prev, enterprise: '' }));
                } else {
                  searchEnterprises(value);
                }
              }}
              onFocus={() => enterpriseSearch.length >= 2 && setShowEnterpriseDropdown(true)}
              placeholder="Rechercher entreprise..."
              className="form-input"
            />
            {showEnterpriseDropdown && enterprises.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {enterprises.map((enterprise) => (
                  <div
                    key={enterprise.id}
                    onClick={() => handleSelectEnterprise(enterprise)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <div className="font-medium">{enterprise.nom}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filtre Mutualiste */}
          <div className="relative" ref={mutualisteRef}>
            <label className="form-label">Mutualiste</label>
            <input
              type="text"
              value={mutualisteSearch}
              onChange={(e) => {
                const value = e.target.value;
                setMutualisteSearch(value);
                if (!value) {
                  setFilters(prev => ({ ...prev, mutualiste: '' }));
                } else {
                  searchMutualistes(value);
                }
              }}
              onFocus={() => mutualisteSearch.length >= 2 && setShowMutualisteDropdown(true)}
              placeholder="Rechercher matricule ou nom..."
              className="form-input"
            />
            {showMutualisteDropdown && mutualistes.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {mutualistes.map((mutualiste) => (
                  <div
                    key={mutualiste.id}
                    onClick={() => handleSelectMutualiste(mutualiste)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <div className="font-medium">{mutualiste.matricule}</div>
                    <div className="text-sm text-gray-600">{mutualiste.nom} {mutualiste.prenoms}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liste des polices */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {policies.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune police trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Police
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mutualiste
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant mensuel
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bénéficiaires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d'effet
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cotisations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {policies.map((policy) => (
                  <tr key={policy.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {policy.policyNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {policy.subscription?.mutualiste?.nom} {policy.subscription?.mutualiste?.prenoms}
                      </div>
                      <div className="text-sm text-gray-500">
                        Matricule: {policy.subscription?.mutualiste?.matricule}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {policy.subscription?.offer?.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {getOfferTypeLabel(policy.subscription?.offer?.type)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(policy.monthlyAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {policy.beneficiariesCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(policy.effectiveDate).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      <div className="text-sm">
                        {policy.receivedContributions}/{policy.expectedContributions}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(policy.totalAmountReceived)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(policy.status)}
                        <span className={`ml-2 badge badge-${getStatusColor(policy.status?.libelle || policy.status)}`}>
                          {getStatusLabel(policy.status?.libelle || policy.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(policy)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleStatusChange(policy)}
                          className="text-green-600 hover:text-green-900"
                          title="Changer le statut"
                        >
                          <PlayCircleIcon className="h-5 w-5" />
                        </button>
                        {/* Bouton de demande de résiliation désactivé pour le moment */}
                        {/* {(policy.status === 'ACTIVE' || policy.status === 'PENDING') && (
                          <button
                            onClick={() => handleRequestTermination(policy)}
                            className="text-red-600 hover:text-red-900"
                            title="Demander la résiliation"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        )} */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> sur{' '}
                  <span className="font-medium">{pagination.pages}</span>
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min={1}
                    max={pagination.pages}
                    value={gotoPage}
                    onChange={(e) => setGotoPage(e.target.value)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Aller à"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const pageNumber = parseInt(gotoPage, 10);
                      if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= pagination.pages) {
                        setCurrentPage(pageNumber);
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Aller
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
                >
                  Préc.
                </button>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {getPaginationItems(currentPage, pagination.pages).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                  disabled={currentPage === pagination.pages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
                >
                  Suiv.
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de changement de statut */}
      {showStatusModal && selectedPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowStatusModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <PlayCircleIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Modifier le statut de la police
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Police n° <strong>{selectedPolicy.policyNumber}</strong>
                      </p>
                      <div className="mt-4">
                        <label className="form-label">Nouveau statut</label>
                        <select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                          className="form-input"
                        >
                          <option value="PENDING">{String('En attente')}</option>
                          <option value="ACTIVE">{String('Active')}</option>
                          <option value="SUSPENDED">{String('Suspendue')}</option>
                          <option value="TERMINATED">{String('Résiliée')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmStatusChange}
                  disabled={updateStatusMutation.isLoading || newStatus === selectedPolicy.status}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateStatusMutation.isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal détails épargne */}
      {showSavingsModal && selectedPolicy && (
        <SavingsDetailsModal
          policy={selectedPolicy}
          onClose={() => {
            setShowSavingsModal(false);
            setSelectedPolicy(null);
          }}
        />
      )}

      {/* Modal de détails (polices, notamment santé) */}
      {showDetailsModal && selectedPolicy && (
        <HealthPolicyDetailsModal
          policy={selectedPolicy}
          onClose={() => setShowDetailsModal(false)}
          onOpenHealthReimbursement={() => openHealthReimbursementModal()}
          onDownloadHealthStatement={() => toast.success('Fonctionnalité à venir : le relevé détaillé des prestations sera bientôt disponible.')}
          onRequestTermination={handleRequestTermination}
        />
      )}

      {/* Modal de création de demande de remboursement santé (prestation + demande) */}
      {showHealthReimbursementModal && selectedPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowHealthReimbursementModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <BanknotesIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full relative">
                    <button
                      type="button"
                      onClick={() => setShowHealthReimbursementModal(false)}
                      className="absolute top-0 right-0 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <span className="sr-only">Fermer</span>
                      ×
                    </button>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 pr-6">
                      Demande de remboursement
                    </h3>
                    <div className="mt-2">
                      <div className="space-y-4">
                        {/* Section Prestation de santé */}
                        <div className="border-b border-gray-200 pb-4 mb-4">
                          <div className="space-y-4">
                            {/* Bénéficiaire (plus large), type de prestation et date sur la même ligne en desktop */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Bénéficiaire <span className="text-red-500">*</span>
                                </label>
                                <select
                                  className="form-input h-11"
                                  value={reimbursementForm.beneficiaryId}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, beneficiaryId: e.target.value }))}
                                >
                                  <option value="">{String('Sélectionner un bénéficiaire')}</option>
                                  {selectedPolicy.subscription?.mutualiste && (
                                    <option value={selectedPolicy.subscription.mutualiste.id}>
                                      {String(`${selectedPolicy.subscription.mutualiste.nom} ${selectedPolicy.subscription.mutualiste.prenoms} (Adhérent)`)}
                                    </option>
                                  )}
                                  {Array.isArray(selectedPolicy.subscription?.mutualiste?.ayantsDroit) && selectedPolicy.subscription.mutualiste.ayantsDroit.map((ayant) => (
                                    <option key={ayant.id} value={ayant.id}>
                                      {String(`${ayant.nom} ${ayant.prenoms}${ayant.lienParente?.libelle ? ` (${ayant.lienParente.libelle})` : ''}`)}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Type de prestation <span className="text-red-500">*</span>
                                </label>
                                <select
                                  className="form-input h-11"
                                  value={reimbursementForm.serviceTypeId}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, serviceTypeId: e.target.value }))}
                                >
                                  <option value="">{String('Sélectionner un type')}</option>
                                  {Array.isArray(healthServiceTypes) && healthServiceTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                      {String(type.libelle)}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Date de prestation <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="date"
                                  className="form-input h-11"
                                  value={reimbursementForm.executedAt}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, executedAt: e.target.value }))}
                                />
                              </div>
                            </div>

                            {/* Établissement et praticien sur la même ligne en desktop */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Établissement <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={reimbursementForm.etablissement}
                                  onChange={(e) => {
                                    const value = (e.target.value || '').toUpperCase();
                                    setReimbursementForm(prev => ({ ...prev, etablissement: value }));
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Praticien <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={reimbursementForm.praticien}
                                  onChange={(e) => {
                                    const value = (e.target.value || '').toUpperCase();
                                    setReimbursementForm(prev => ({ ...prev, praticien: value }));
                                  }}
                                />
                              </div>
                            </div>

                            {/* Montants sur une seule ligne en desktop */}
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Montant centre <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={reimbursementForm.costTotal}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, costTotal: e.target.value }))}
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Montant conv. <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={reimbursementForm.costConventionne}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, costConventionne: e.target.value }))}
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Part mutuelle <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={reimbursementForm.assurancePrincipalePart}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, assurancePrincipalePart: e.target.value }))}
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Ticket mod. <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={reimbursementForm.ticketModerateur}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, ticketModerateur: e.target.value, amountTicketModerateur: e.target.value }))}
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Surcoût <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={reimbursementForm.surcout}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, surcout: e.target.value, amountSurcout: e.target.value }))}
                                  min="0"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Section Demande de remboursement */}
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Type de remboursement <span className="text-red-500">*</span>
                              </label>
                              <select
                                className="form-input h-11"
                                value={reimbursementForm.typeId}
                                onChange={(e) => setReimbursementForm(prev => ({ ...prev, typeId: e.target.value }))}
                              >
                                <option value="">{String('Sélectionner un type de remboursement')}</option>
                                {healthRequestTypes.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {String(type.libelle)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Justificatifs <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="file"
                                multiple
                                className="form-input"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files || []);
                                  setReimbursementForm(prev => ({ ...prev, attachmentFile: files }));
                                }}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Commentaires
                            </label>
                            <textarea
                              value={terminationNotes}
                              onChange={(e) => {
                                const value = (e.target.value || '').toUpperCase();
                                setTerminationNotes(value);
                              }}
                              rows={2}
                              className="form-input"
                              placeholder="Informations complémentaires..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCreateHealthReimbursement}
                  disabled={createHealthReimbursementMutation.isLoading
                    || !reimbursementForm.beneficiaryId
                    || !reimbursementForm.serviceTypeId
                    || !reimbursementForm.executedAt
                    || !reimbursementForm.etablissement
                    || !reimbursementForm.praticien
                    || !reimbursementForm.costTotal
                    || !reimbursementForm.costConventionne
                    || !reimbursementForm.assurancePrincipalePart
                    || !reimbursementForm.ticketModerateur
                    || !reimbursementForm.typeId
                    || !reimbursementForm.attachmentFile
                    || reimbursementForm.attachmentFile.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createHealthReimbursementMutation.isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Créer la demande
                </button>
                <button
                  type="button"
                  onClick={() => setShowHealthReimbursementModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPolicies;
